import { projectFromFirestore } from '@parallel-fluidics/shared/models';
import { getPPartLabel } from '@parallel-fluidics/shared/models/projects/parts';
import fetchProjectPart from 'features/pPart/api/fetchProjectPart';
import { doc, getDoc, getFirestore } from 'firebase/firestore';

/**
 * Resolve the standard part label ("P068-0068-A-COC (Pipette interface
 * - Product)") for each lot's part, keyed by lot doc ID. Fetches each
 * distinct project and part once. Graceful: a lot whose project or part
 * can't be fetched simply has no entry — callers fall back to raw IDs.
 *
 * @param {Array<object>} lots - Lot models (projectDocId / pPartDocId).
 * @returns {Promise<Object<string, string>>} lotId → label.
 */
export default async function fetchPartLabelsForLots(lots) {
  const db = getFirestore();
  const withPart = lots.filter((lot) => lot.projectDocId && lot.pPartDocId);

  const projectNameById = {};
  await Promise.all(
    [...new Set(withPart.map((lot) => lot.projectDocId))].map(async (projectDocId) => {
      try {
        const snap = await getDoc(doc(db, 'projects', projectDocId));
        if (snap.exists()) projectNameById[projectDocId] = projectFromFirestore(snap).name;
      } catch {
        // Label falls back to the part id without a project prefix.
      }
    }),
  );

  const labelByPartKey = {};
  await Promise.all(
    [...new Set(withPart.map((lot) => `${lot.projectDocId}/${lot.pPartDocId}`))]
      .map(async (partKey) => {
        const [projectDocId, pPartDocId] = partKey.split('/');
        try {
          const part = await fetchProjectPart({ projectDocId, pPartDocId });
          labelByPartKey[partKey] = getPPartLabel(part, projectNameById[projectDocId]);
        } catch {
          // No entry — the caller shows the raw IDs instead.
        }
      }),
  );

  return Object.fromEntries(
    withPart
      .filter((lot) => labelByPartKey[`${lot.projectDocId}/${lot.pPartDocId}`])
      .map((lot) => [lot.id, labelByPartKey[`${lot.projectDocId}/${lot.pPartDocId}`]]),
  );
}
